import { AxiosInstance } from "axios"
import { ServiceResponse } from "../../../../core/api/apiTypes"
import { DigitalSignage, MenuDetails, MenuPlanningList, MerchantConfig } from "./menuPlanningTypes"

export enum MenuPlaningQuery {
    LIST = "menus-for-merchant",
    DETAILS = "details-for-menu",
    DIGITAL_SIGNAGE = "digital-signage",
    MERCHANT_CONFIG = "merchant-config",
}

type MenusListParams = Readonly<{
    merchantId: number
    from?: string
    to?: string
    isActive?: boolean
}>

export type MenusListResponse = {
    menuPlanning: MenuPlanningList
}

export async function fetchMenusList(
    { merchantId, from, to, isActive }: MenusListParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<MenusListResponse> {
    const url = `/merchants/${merchantId}/menus`

    const response = await axios.get(url, {
        params: { from, to, isActive },
        signal,
    })

    return response.data
}

export type DigitalSignageResponse = {
    signs: Array<DigitalSignage>
}

export async function fetchDigitalSignageList(
    { merchantId }: { merchantId: number },
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<DigitalSignageResponse> {
    const url = `/merchants/${merchantId}/delfiSigns/preview`

    const response = await axios.get(url, {
        signal,
    })

    return response.data
}

export async function switchMenuStatus(
    { menuId }: { menuId: number },
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceResponse<{}>> {
    const { data } = await axios.post(`/menus/${menuId}/switchStatus`, {
        signal,
    })
    return data
}

type DeleteMenuParams = Readonly<{
    menuId: number
}>

export async function deleteMenu(
    { menuId }: DeleteMenuParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceResponse<{}>> {
    const { data } = await axios.delete(`/menus/${menuId}`, {
        signal,
    })
    return data
}

type MenuDetailsParams = Readonly<{
    menuId: number
}>

export async function fetchMenuDetails(
    { menuId }: MenuDetailsParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<MenuDetails> {
    const url = `/menus/${menuId}`

    const response = await axios.get(url, {
        signal,
    })

    return response.data
}

export async function createDayMenu(
    { menu, merchantId }: { menu: MenuDetails; merchantId: number },
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceResponse<{}>> {
    const { data } = await axios.post(
        `/menus`,
        {
            ...menu,
            merchant: {
                id: merchantId,
            },
        },
        {
            signal,
        },
    )
    return data
}

export async function updateDayMenu(
    { menu, menuId }: { menu: MenuDetails; menuId: number },
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceResponse<{}>> {
    const { data } = await axios.post(
        `/menus/${menuId}`,
        {
            ...menu,
        },
        {
            signal,
        },
    )
    return data
}

type FetchMerchantConfigParams = Readonly<{
    merchantId: number
}>

export async function fetchMerchantConfig(
    { merchantId }: FetchMerchantConfigParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<MerchantConfig> {
    const url = `/merchants/${merchantId}/config`

    const response = await axios.get(url, {
        signal,
    })

    return response.data
}

type SaveMerchantConfigParams = Readonly<{
    merchantId: number
    property: {
        name: string
        value: string
    }
}>

export async function saveMerchantConfig(
    { merchantId, property }: SaveMerchantConfigParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceResponse<{}>> {
    const url = `/merchants/${merchantId}/config`

    const response = await axios.post(
        url,
        { property },
        {
            signal,
        },
    )

    return response.data
}

export async function clearDelfiSigns(
    { id }: { id: number },
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceResponse<{}>> {
    const url = `/delfiSigns/${id}/clear`

    const response = await axios.post(
        url,
        {},
        {
            signal,
        },
    )

    return response.data
}
